import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import SceneProvider from '@/providers/SceneProvider';
import type { DeltafaceConfig } from '@/utils/scans/deltafaceLoader/types';
import { useToggle } from '@/hooks/useToggle';
import { useNumber } from '@/hooks/useNumber';
import { showErrorToaster } from '@/utils/toasters';
import { parseDeltafaceZipFile } from '@/utils/scans/deltafaceLoader/utils';
import { buildDeltafaceConfig } from '@/utils/scans/deltafaceLoader';
import SequenceSlider from '@/components/SequenceSlider';
import LoadingProgress from '@/components/LoadingProgress';
import DeltafaceStepViewer from './DeltafaceStepViewer';
import { classes } from './utils';

export type SweethSequenceViewerProps = {
  className?: string;
  file: File | Blob;
};

export default function SweethSequenceViewer({
  className,
  file,
}: SweethSequenceViewerProps) {
  const [config, setConfig] = useState<DeltafaceConfig>();
  const [activeStep, setActiveStep] = useNumber(0);
  const [stepsCount, setStepsCount] = useNumber(0);
  const [loadingProgress, setLoadingProgress] = useNumber(0);
  const [isLoadingFiles, toggleLoadingFiles] = useToggle();

  useEffect(() => {
    if (file) handleParseFile();
  }, [file]);

  const handleParseFile = async () => {
    try {
      toggleLoadingFiles(true);
      const data = await parseDeltafaceZipFile(file!, (update) =>
        setLoadingProgress((p) => Math.max(p, update))
      );
      const deltafaceConfig = buildDeltafaceConfig(data, (update) =>
        setLoadingProgress((p) => Math.max(p, update))
      );
      setStepsCount(deltafaceConfig.totalSteps);
      setConfig(deltafaceConfig);
      toggleLoadingFiles(false);
    } catch (err) {
      showErrorToaster('Error loading file');
      throw err;
    }
  };

  return (
    <div className={twMerge(classes.wrapper, className)}>
      <SceneProvider backgroundColor={0xcbd1d6}>
        {isLoadingFiles && (
          <LoadingProgress
            label="Loading sequence"
            progress={loadingProgress}
          />
        )}
        {config && !isLoadingFiles && (
          <DeltafaceStepViewer
            className={classes.viewer}
            step={activeStep}
            initialConfig={config}
          />
        )}
      </SceneProvider>
      <SequenceSlider
        position={activeStep}
        stepsCount={stepsCount}
        onSelect={setActiveStep}
      />
    </div>
  );
}
